/**
 * store/index.ts
 * ─────────────────────────────────────────────────────────────
 * Zustand global state store.
 *
 * Slices:
 *  - scanner:  gapper list, scan status, filters
 *  - ticker:   selected ticker + analysis data
 *  - account:  balance, phase, stats
 *  - ws:       WebSocket connection state
 *  - ui:       active tab, panel state
 * ─────────────────────────────────────────────────────────────
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  Gapper, TechnicalSignals, OptionsChain,
  TradePlan, ScoreBreakdown, NewsItem, AccountStats, Trade,
  OptionsQualCheck,
} from '../types';

// ─── Scanner Slice ────────────────────────────────────────────

interface ScannerState {
  gappers:       Gapper[];
  isScanning:    boolean;
  lastScanTime:  number | null;
  error:         string | null;
  optionsChecks: Record<string, OptionsQualCheck>;
  setGappers:      (g: Gapper[])                        => void;
  upsertGapper:    (g: Gapper)                           => void;
  setScanning:     (v: boolean)                          => void;
  setLastScan:     (t: number)                           => void;
  setScanError:    (e: string | null)                    => void;
  setOptionsCheck: (sym: string, c: OptionsQualCheck)    => void;
  clearOptionsChecks: ()                                 => void;
}

// ─── Selected Ticker Slice ────────────────────────────────────

interface TickerState {
  sym:          string | null;
  gapper:       Gapper | null;
  signals:      TechnicalSignals | null;
  chain:        OptionsChain | null;
  plan:         TradePlan | null;
  score:        ScoreBreakdown | null;
  news:         NewsItem[];
  aiAnalysis:   string | null;
  aiLoading:    boolean;
  aiMessages:   Array<{ role: 'user' | 'assistant'; content: string }>;
  isLoading:    boolean;
  error:        string | null;
  selectTicker:   (sym: string, gapper: Gapper)    => void;
  setSignals:     (s: TechnicalSignals)             => void;
  setChain:       (c: OptionsChain)                 => void;
  setPlan:        (p: TradePlan)                    => void;
  setScore:       (s: ScoreBreakdown)               => void;
  setNews:        (n: NewsItem[])                   => void;
  setAiAnalysis:  (a: string | null)                => void;
  setAiLoading:   (v: boolean)                      => void;
  addAiMessage:   (role: 'user' | 'assistant', content: string) => void;
  clearAiMessages: () => void;
  setLoading:     (v: boolean)                      => void;
  setError:       (e: string | null)                => void;
  clearTicker:    () => void;
}

// ─── Account Slice ────────────────────────────────────────────

interface AccountState {
  stats:       AccountStats | null;
  trades:      Trade[];
  isLoading:   boolean;
  setStats:    (s: AccountStats) => void;
  setTrades:   (t: Trade[])      => void;
  addTrade:    (t: Trade)        => void;
  updateTrade: (id: string, updates: Partial<Trade>) => void;
  setLoading:  (v: boolean)      => void;
}

// ─── WebSocket Slice ──────────────────────────────────────────

type WsStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface WsState {
  status:    WsStatus;
  lastPing:  number | null;
  setStatus: (s: WsStatus)     => void;
  setPing:   (t: number)       => void;
}

// ─── UI Slice ─────────────────────────────────────────────────

// Journal + Analytics moved out to their own top-level tab (see MainTab) —
// RightPanel now only holds AI / News (the Sizer tab was removed once the
// Auto-Trader tab's own risk limits covered real position sizing).
type RightPanelTab = 'ai' | 'news';
export type MainTab = 'scanner' | 'journal' | 'autotrader';

const MAIN_TAB_STORAGE_KEY = 'phase1-main-tab';

function loadStoredMainTab(): MainTab {
  try {
    const stored = localStorage.getItem(MAIN_TAB_STORAGE_KEY);
    return stored === 'journal' || stored === 'autotrader' ? stored : 'scanner';
  } catch {
    return 'scanner'; // localStorage unavailable (private mode, etc.) — fall back silently
  }
}

interface UiState {
  mainTab:      MainTab;
  rightTab:     RightPanelTab;
  sidebarOpen:  boolean;
  setMainTab:   (t: MainTab) => void;
  setRightTab:  (t: RightPanelTab) => void;
  toggleSidebar: () => void;
}

// ─── Combined Store ───────────────────────────────────────────

interface AppStore extends ScannerState, TickerState, AccountState, WsState, UiState {}

export const useStore = create<AppStore>()(
  devtools(
    (set, get) => ({

      // ── Scanner ─────────────────────────────────────────────
      gappers:       [],
      isScanning:    false,
      lastScanTime:  null,
      error:         null,
      optionsChecks: {},
      setGappers:       (gappers)      => set({ gappers }),
      upsertGapper:     (gapper)       => set(s => {
        const i = s.gappers.findIndex(g => g.sym === gapper.sym);
        if (i === -1) return { gappers: [...s.gappers, gapper] };
        const next = s.gappers.slice();
        next[i] = gapper;
        return { gappers: next };
      }),
      setScanning:      (isScanning)   => set({ isScanning }),
      setLastScan:      (lastScanTime) => set({ lastScanTime }),
      setScanError:     (error)        => set({ error }),
      setOptionsCheck:  (sym, check)   => set(s => ({ optionsChecks: { ...s.optionsChecks, [sym]: check } })),
      clearOptionsChecks: ()           => set({ optionsChecks: {} }),

      // ── Selected Ticker ──────────────────────────────────────
      sym:         null,
      gapper:      null,
      signals:     null,
      chain:       null,
      plan:        null,
      score:       null,
      news:        [],
      aiAnalysis:  null,
      aiLoading:   false,
      aiMessages:  [],
      isLoading:   false,

      selectTicker: (sym, gapper) => set({
        sym, gapper,
        signals: null, chain: null, plan: null, score: null,
        news: [], aiAnalysis: null, aiMessages: [], error: null,
      }),
      setSignals:     (signals)    => set({ signals }),
      setChain:       (chain)      => set({ chain }),
      setPlan:        (plan)       => set({ plan }),
      setScore:       (score)      => set({ score }),
      setNews:        (news)       => set({ news }),
      setAiAnalysis:  (aiAnalysis) => set({ aiAnalysis }),
      setAiLoading:   (aiLoading)  => set({ aiLoading }),
      addAiMessage:   (role, content) =>
        set(s => ({ aiMessages: [...s.aiMessages, { role, content }] })),
      clearAiMessages: () => set({ aiMessages: [] }),
      setLoading:     (isLoading)  => set({ isLoading }),
      setError:       (error)      => set({ error }),
      clearTicker:    () => set({
        sym: null, gapper: null, signals: null, chain: null,
        plan: null, score: null, news: [], aiAnalysis: null, aiMessages: [],
      }),

      // ── Account ──────────────────────────────────────────────
      stats:    null,
      trades:   [],
      setStats: (stats)  => set({ stats }),
      setTrades: (trades) => set({ trades }),
      addTrade:  (trade)  => set(s => ({ trades: [trade, ...s.trades] })),
      updateTrade: (id, updates) => set(s => ({
        trades: s.trades.map(t => t.id === id ? { ...t, ...updates } : t)
      })),

      // ── WebSocket ────────────────────────────────────────────
      status:    'disconnected',
      lastPing:  null,
      setStatus: (status) => set({ status }),
      setPing:   (lastPing) => set({ lastPing }),

      // ── UI ───────────────────────────────────────────────────
      mainTab:     loadStoredMainTab(),
      rightTab:    'ai',
      sidebarOpen: true,
      setMainTab: (mainTab) => {
        try { localStorage.setItem(MAIN_TAB_STORAGE_KEY, mainTab); } catch { /* private mode etc — non-fatal */ }
        set({ mainTab });
      },
      setRightTab:   (rightTab) => set({ rightTab }),
      toggleSidebar: () => set(s => ({ sidebarOpen: !s.sidebarOpen })),
    }),
    { name: 'phase1-trader' }
  )
);

// Convenience selectors
export const useScanner  = () => useStore(s => ({
  gappers: s.gappers, isScanning: s.isScanning,
  lastScanTime: s.lastScanTime, error: s.error,
  optionsChecks: s.optionsChecks,
  setGappers: s.setGappers, upsertGapper: s.upsertGapper, setScanning: s.setScanning,
  setLastScan: s.setLastScan, setScanError: s.setScanError,
  setOptionsCheck: s.setOptionsCheck, clearOptionsChecks: s.clearOptionsChecks,
}));

export const useTicker   = () => useStore(s => ({
  sym: s.sym, gapper: s.gapper, signals: s.signals,
  chain: s.chain, plan: s.plan, score: s.score,
  news: s.news, aiAnalysis: s.aiAnalysis, aiLoading: s.aiLoading,
  aiMessages: s.aiMessages, isLoading: s.isLoading, error: s.error,
  selectTicker: s.selectTicker, setSignals: s.setSignals,
  setChain: s.setChain, setPlan: s.setPlan, setScore: s.setScore,
  setNews: s.setNews, setAiAnalysis: s.setAiAnalysis,
  setAiLoading: s.setAiLoading, addAiMessage: s.addAiMessage,
  clearAiMessages: s.clearAiMessages, setLoading: s.setLoading, setError: s.setError,
}));

export const useAccount  = () => useStore(s => ({
  stats: s.stats, trades: s.trades,
  setStats: s.setStats, setTrades: s.setTrades,
  addTrade: s.addTrade, updateTrade: s.updateTrade,
}));

export const useWs       = () => useStore(s => ({
  status: s.status, lastPing: s.lastPing,
  setStatus: s.setStatus, setPing: s.setPing,
}));

export const useUi       = () => useStore(s => ({
  mainTab: s.mainTab, rightTab: s.rightTab, sidebarOpen: s.sidebarOpen,
  setMainTab: s.setMainTab, setRightTab: s.setRightTab, toggleSidebar: s.toggleSidebar,
}));
